import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Interceptor: when a request fails with a Cloudflare error page (HTML body),
// replace `response.data.detail` with a short friendly message so callers using
// formatApiErrorDetail() don't accidentally render Cloudflare's raw HTML in a toast.
api.interceptors.response.use(
  (r) => r,
  (error) => {
    try {
      const resp = error?.response;
      if (resp) {
        const ct = String(resp.headers?.["content-type"] || "").toLowerCase();
        const body = resp.data;
        const looksHtml = ct.includes("text/html")
          || (typeof body === "string" && (body.trim().startsWith("<") || body.includes("<!DOCTYPE")));
        if (looksHtml) {
          const code = resp.status;
          const cfMsg = code === 520 ? "The server took too long to respond. Please try again in a few seconds."
            : code === 502 ? "Server is warming up. Please try again."
            : code === 503 ? "Server is temporarily unavailable. Please try again shortly."
            : code === 504 ? "The request timed out. Please try again."
            : "Network hiccup — please try again.";
          resp.data = { detail: cfMsg, __cloudflare_error: true, status: code };
        }
      }
    } catch { /* swallow */ }
    return Promise.reject(error);
  },
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  let raw;
  if (typeof detail === "string") {
    raw = detail;
  } else if (Array.isArray(detail)) {
    raw = detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  } else if (detail && typeof detail.msg === "string") {
    raw = detail.msg;
  } else {
    raw = String(detail);
  }
  // Bulletproof: swap out any Cloudflare-style origin-timeout blurb that
  // slipped past the interceptor (older cached JS bundles, malformed responses).
  if (/origin web server|cloudflare|<!DOCTYPE|<html/i.test(raw)) {
    return "The server took too long to respond. Please wait a few seconds and try again.";
  }
  return raw;
}

/**
 * Normalize an image URL so legacy portfolio/logo entries stored with a
 * stale hostname (e.g. an old preview domain, the pre-rebrand backend host)
 * still resolve when rendered from the current site.
 *
 * Behaviour:
 * - Empty / falsy → returns "" (caller should render a placeholder).
 * - Data URIs / blob URIs → passed through untouched.
 * - Starts with `/api/...` (relative) → prefixed with the current backend origin.
 * - Absolute URL whose path starts with `/api/uploads/` → hostname replaced
 *   with the current backend origin (this fixes the production-broken-images
 *   case where uploads were saved under the pre-rebrand hostname).
 * - Any other absolute URL (external CDN, Unsplash, Pexels) → passed through.
 */
export function resolveImageSrc(url) {
  if (!url || typeof url !== "string") return "";
  const trimmed = url.trim();
  if (!trimmed) return "";
  if (trimmed.startsWith("data:") || trimmed.startsWith("blob:")) return trimmed;
  if (trimmed.startsWith("/api/")) return `${BACKEND_URL}${trimmed}`;
  if (trimmed.startsWith("/")) return `${BACKEND_URL}${trimmed}`;
  try {
    const parsed = new URL(trimmed);
    // Only rewrite our own upload URLs — leave third-party CDNs alone.
    if (parsed.pathname.startsWith("/api/uploads/")) {
      const currentOrigin = new URL(BACKEND_URL).origin;
      if (parsed.origin !== currentOrigin) {
        return `${currentOrigin}${parsed.pathname}${parsed.search}`;
      }
    }
    return trimmed;
  } catch {
    return trimmed;
  }
}

export default api;
